import Card, { CardDeadEndHint } from '@/components/Card';
import { ConditionalRender } from '@/components/ui/ConditionalRender';
import { Hint } from '@/components/ui/Hint';
import { useStepper } from '@/features/v2/lib/stepper';
import { InputVehicleSoldPrice } from '@/features/v2/vehicle-damage/components/InputVehicleSoldPrice';
import {
  ACCIDENT_IN_POLAND_NAME,
  RadioGroupAccidentInPoland,
} from '@/features/v2/vehicle-damage/components/RadioGroupAccidentInPoland';
import {
  AGREEMENT_OR_SETTLEMENT,
  AGREEMENT_OR_SETTLEMENT_NAME,
  RadioGroupAgreementOrSettlement,
} from '@/features/v2/vehicle-damage/components/RadioGroupAgreementOrSettlement';
import {
  INSURER_PAY_COMPENSATION_NAME,
  RadioGroupInsurerPayCompensation,
} from '@/features/v2/vehicle-damage/components/RadioGroupInsurerPayCompensation';
import {
  VEHICLE_VALUE_BEFORE_CRASH,
  VEHICLE_VALUE_BEFORE_CRASH_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupVehicleValueBeforeCrash';
import {
  RadioGroupWheelSide,
  WHEEL_SIDE,
  WHEEL_SIDE_NAME,
} from '@/features/v2/vehicle-damage/components/RadioGroupWheelSide';
import {
  RadioGroupWheelSideForSure,
  WHEEL_SIDE_FACTORY_INSTALLED,
} from '@/features/v2/vehicle-damage/components/RadioGroupWheelSideForSure';
import { SelectAgreementOrSettlementConcluded } from '@/features/v2/vehicle-damage/components/SelectAgreementOrSettlementConcluded';
import {
  CAR_CONDITION,
  CAR_CONDITION_NAME,
  SelectCarCondition,
} from '@/features/v2/vehicle-damage/components/SelectCarCondition';
import { VehicleDamageSchemaDeadEndsStepOne } from '@/features/v2/vehicle-damage/stepsSchema';

import { i18n } from '@lingui/core';

export const FullAC = ({ innerStep }: { innerStep: number }) => {
  const { step } = useStepper();

  return (
    <>
      {(step > 0 || innerStep >= 1) && (
        <>
          <ConditionalRender
            when={(val) =>
              val[VEHICLE_VALUE_BEFORE_CRASH_NAME] ===
              VEHICLE_VALUE_BEFORE_CRASH.UPPER_10k
            }
          >
            <Card>
              <RadioGroupAccidentInPoland />
            </Card>
          </ConditionalRender>
          <ConditionalRender when={(val) => val[ACCIDENT_IN_POLAND_NAME]}>
            <Card
              isDeadEnd={
                VehicleDamageSchemaDeadEndsStepOne
                  .fullACGoingToBeSoldDamagedCarCondition.condition
              }
            >
              <SelectCarCondition />
              <ConditionalRender
                when={(val) =>
                  val[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_REPAIRED ||
                  val[CAR_CONDITION_NAME] === CAR_CONDITION.SOLD_DAMAGED
                }
              >
                <div className={'flex flex-col gap-3 lg:grid lg:grid-cols-2 '}>
                  <InputVehicleSoldPrice />
                  <Hint className={'min-h-[52px] place-self-end'}>
                    {i18n.t(`W dalszej części poprosimy Cię o wgranie umowy sprzedaży
                samochodu.`)}
                  </Hint>
                </div>
              </ConditionalRender>
              <CardDeadEndHint variant="primary">
                <Hint.Title>
                  {i18n.t('Twoja sytuacja jest dla nas szczególnie istotna')}
                </Hint.Title>
                <Hint.Description className="caption-large lg:text-xs">
                  {i18n.t(
                    'Chcielibyśmy zadać Ci kilka pytań, zanim przejdziemy dalej. W tym celu skontaktujemy się z Tobą wkrótce telefonicznie, aby omówić szczegóły. Do usłyszenia!'
                  )}
                </Hint.Description>
              </CardDeadEndHint>
            </Card>
          </ConditionalRender>
          {(innerStep >= 2 || step > 0) && (
            <>
              <ConditionalRender when={(val) => !!val[CAR_CONDITION_NAME]}>
                <Card>
                  <RadioGroupWheelSide />
                  <ConditionalRender
                    when={(val) => val[WHEEL_SIDE_NAME] === WHEEL_SIDE.LEFT}
                  >
                    <RadioGroupWheelSideForSure />
                  </ConditionalRender>
                </Card>
              </ConditionalRender>
              <ConditionalRender
                when={(val) =>
                  val[WHEEL_SIDE_NAME] === WHEEL_SIDE.RIGHT ||
                  !!val[WHEEL_SIDE_FACTORY_INSTALLED]
                }
              >
                <Card
                  isDeadEnd={
                    VehicleDamageSchemaDeadEndsStepOne
                      .fullAgreementOrSettlementConcluded.condition
                  }
                  addBorderWhen={(val) =>
                    val[INSURER_PAY_COMPENSATION_NAME] &&
                    val[AGREEMENT_OR_SETTLEMENT_NAME] ===
                      AGREEMENT_OR_SETTLEMENT.NO
                  }
                >
                  <RadioGroupInsurerPayCompensation />
                  <ConditionalRender
                    when={(val) => !!val[INSURER_PAY_COMPENSATION_NAME]}
                  >
                    <RadioGroupAgreementOrSettlement />
                    <ConditionalRender
                      when={(val) =>
                        val[AGREEMENT_OR_SETTLEMENT_NAME] ===
                        AGREEMENT_OR_SETTLEMENT.YES
                      }
                    >
                      <SelectAgreementOrSettlementConcluded />
                    </ConditionalRender>
                  </ConditionalRender>
                  <ConditionalRender
                    when={(val) =>
                      val[INSURER_PAY_COMPENSATION_NAME] &&
                      val[AGREEMENT_OR_SETTLEMENT_NAME] ===
                        AGREEMENT_OR_SETTLEMENT.NO
                    }
                  >
                    <Hint variant="primary">
                      <Hint.Title>
                        {i18n.t(
                          'Ubezpieczyciele coraz częściej przesyłają kosztorysy połączone z ugodą'
                        )}
                      </Hint.Title>
                      {i18n.t(
                        `Nazywają to “likwidacją szkody w trybie uproszczonym”. Uważaj także na telefoniczne próby zawarcia ugody i nie potwierdzaj podczas rozmowy, że proponowana kwota wystarczy na naprawę pojazdu. Potwierdzenie może być potraktowane jako ugoda, co zablokuje możliwość do uzyskania wyższego odszkodowania.`
                      )}
                    </Hint>
                  </ConditionalRender>
                  <CardDeadEndHint variant="primary">
                    <Hint.Title>
                      {i18n.t(
                        'Niestety, nie możemy podjąć dalszych działań w Twojej sprawie'
                      )}
                    </Hint.Title>
                    <Hint.Description>
                      <p>
                        {i18n.t(
                          'Zawarcie ugody na koszty naprawy oznacza, że doszło do porozumienia w sprawie odszkodowania za poniesioną szkodę. Ugodę traktuje się jako pełne i ostateczne zadowolenie z kwoty, co wyklucza możliwość dochodzenia dodatkowych środków.'
                        )}
                      </p>
                      <p>
                        {i18n.t(
                          'Jeśli nie zawarto ugody, a ubezpieczyciel wypłacił Ci tylko kwotę bezsporną z kosztorysu, zmień odpowiedź i przejdź do kolejnego etapu.'
                        )}
                      </p>
                    </Hint.Description>
                  </CardDeadEndHint>
                </Card>
              </ConditionalRender>
            </>
          )}
        </>
      )}
    </>
  );
};
